<template>
  <div class="dashboard-control">
    <div class="navigation--area">
      <button class="navigation prev"
              :class="{ disabled: domainIndex < 1 || isDemo}"
              @click="indexPrev">
        <i class="icon dashboard--arrow-left"></i>
      </button>

      <div class="dropdown"
           :class="{ disabled: isDemo }"
           v-if="domains.length"
           id="dashboardControlDropdown"
           @mouseenter="resetDropdownTimer"
           @scroll.stop>
        <div class="selected" @click="dropdownOpen = true" :class="{ disabled: isDemo }">
          <span class="index">
            <span class="current">{{ domainIndex + 1 }}</span><span class="slash">/</span>{{ domains.length }}
          </span>
          <span class="label">
            {{ domains[domainIndex].domainName }}
          </span>
          <i class="icon icon--arrow-updown"></i>
        </div>
        <div class="item-list" @mouseleave="startDropdownTimer" :class="{ active: dropdownOpen }">
          <div class="item-list-wrap">
            <button class="item-list-item"
                    v-for="(domain, index) in domains"
                    :key="`domain${index}`"
                    :class="{ active: domainIndex === index }"
                    @click="setIndex(index)">
              <span class="index">{{ index + 1 }}</span>
              <div class="type">
                <span :class="['type-label', domain.matchType]">{{ domain.matchType }}</span>
              </div>
              <span class="label">{{ domain.domainName }}</span>
            </button>
          </div>
        </div>
      </div>

      <button class="navigation next"
              :class="{ disabled: (domainIndex > domains.length - 2) || isDemo }"
              @click="indexNext">
        <i class="icon dashboard--arrow-right"></i>
      </button>
    </div>
    <div class="button--area">
      <div class="tracking--area">
        <div class="tracking">
          <div class="dropdown" @scroll.stop>
            <div class="selected" @click="trackingDropdownOpen = true">
            <span class="label" :class="$i18n.locale">
              {{ $t('common.trackingCode') }}
            </span>
              <i class="icon icon--arrow-dropdown"></i>
            </div>
            <div class="item-list" @mouseenter="resetDropdownTimer" @mouseleave="startDropdownTimer"
                 :class="{ active: trackingDropdownOpen }">
              <div class="item-list-item" @click="checkTracking('install')">
                <span class="label">{{ $t('dashboard.trackingCode.checkInstall') }}</span>
              </div>
              <div class="item-list-item" @click="checkTracking('code')">
                <span class="label">{{ $t('dashboard.trackingCode.codeGuide') }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="register-button">
        <button class="btf-button icon" :disabled="!canRegister || isDemo" @click="go({ name: 'add' })" @mouseenter="btnState = 'hover'" @mouseleave="btnState = 'normal'">
          <i class="icon" :class="btnState === 'hover' ? 'icon--plus-white' : 'icon--plus-red'"></i>
          <span class="text ko">&nbsp;{{ $t('common.register') }}</span>
        </button>
        <template v-if="!canRegister">
          <ui-tooltip v-if="planData.isUnpaid"
                      arrowLeftSide>
            <span v-html="$t('dashboard.register.unpaid')"></span>
          </ui-tooltip>
          <ui-tooltip v-else
                      arrowLeftSide>
            <span v-html="$t('dashboard.register.free')"></span>
          </ui-tooltip>
        </template>
      </div>
    </div>
    <div class="type--area">
      <button class="switch">
        <label>
          <i class="icon dashboard--list"></i>
        </label>
        <label class="on">
          <i class="icon dashboard--pane"></i>
        </label>
      </button>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { EventBus } from '@/libs/event-bus'
import UiTooltip from '@/components/ui/UiTooltip'

export default {
  name: 'DashboardControl',
  components: {
    UiTooltip
  },
  props: [
    'domains',
    'domainIndex'
  ],
  data () {
    return {
      btnState: 'normal',
      dropdownTimer: null,
      dropdownTimerTime: null,
      dropdownOpen: false,
      trackingDropdownOpen: false
    }
  },
  watch: {
    dropdownOpen (value) {
      let bodyListener = (e) => {
        let outside = true

        function composedPath (el) {
          let paths = []
          while (el) {
            paths.push(el)
            if (el.tagName === 'HTML') {
              paths.push(document)
              paths.push(window)
              return paths
            }
            el = el.parentElement
          }
        }

        const path = e.path || (e.composedPath && e.composedPath()) || composedPath(e.target)
        if (path) {
          for (const el of path) {
            if (el.id && el.id === 'dashboardControlDropdown') {
              outside = false
            }
          }
        }
        if (outside) {
          this.dropdownOpen = false
        }
      }

      if (value) {
        document.body.addEventListener('click', bodyListener, false)
      } else {
        document.body.removeEventListener('click', bodyListener)
      }
    }
  },
  created () {
    if (this.journeyOptions && this.journeyOptions.sid) {
      for (const [index, domain] of this.domains.entries()) {
        if (domain.sid === this.journeyOptions.sid) {
          this.setIndex(index)
          break
        }
      }
    }

    document.body.addEventListener('click', ({ target }) => {
      if (!target.className.match('selected')) {
        this.trackingDropdownOpen = false
      }
    })
  },
  methods: {
    go (params) {
      EventBus.$emit('onLoading', true)
      setTimeout(() => {
        this.$router.push(params)
      }, 300)
    },
    indexPrev () {
      this.$emit('indexPrev')
    },
    indexNext () {
      this.$emit('indexNext')
    },
    setIndex (index) {
      this.dropdownOpen = false
      this.$emit('setIndex', index)
    },
    checkTracking (value) {
      this.trackingDropdownOpen = false
      if (value === 'install') this.$emit('setCheckInstallSid', true)
      if (value === 'code') this.$emit('setTrackingCodeSid', true)
    },
    startDropdownTimer: function () {
      this.dropdownTimer = setInterval(() => {
        if (this.dropdownTimerTime < 3) {
          this.dropdownOpen = false
          this.trackingDropdownOpen = false
        }
        this.dropdownTimerTime--
      }, 1000)
    },
    resetDropdownTimer: function () {
      this.dropdownTimerTime = 3
      clearInterval(this.dropdownTimer)
      this.dropdownTimer = null
    }
  },
  computed: {
    ...mapState([
      'planData',
      'journeyOptions',
      'isDemo'
    ]),
    canRegister () {
      if (!this.planData.isUnpaid) {
        return true
      } else {
        return this.domains.length < 1
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.btf-button {
  position: relative;
  display: inline-flex;
  border: 1px solid #EC0047;
  height:38px;
  font-size: 14px;
  font-weight: 500;

  justify-content: center;
  align-items: center;
  gap: 10px;

  color: #EC0047;

  padding: 0 14px;
  border-radius: 20px;
  background-color: #fff;
  &:hover {
    background-color: #EC0047;
    color: #fff;
  }
  &:disabled, &[disabled] {
    opacity: .2;
    pointer-events: none;
  }
}
.dashboard-control {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 20px;
  border-bottom: 1px solid #AAAAAA;
  .button--area {
    display: flex;
    gap: 41px;
    .tracking--area {
      position: relative;
      &:after {
        content: "";
        position: absolute;
        top: 50%;
        right: -20px;
        transform: translateY(-50%);
        width: 1px;
        height: 20px;
        background-color: #CCCCCC;
      }
      .tracking {
        position: relative;
        width: 178px;
        .icon--new {
          position: absolute;
          top: -8px;
          left: -6px;
          z-index: 5;
        }
        .dropdown {
          position: relative;
          .selected {
            height: 16px;
            padding: 10px 12px;
            cursor: pointer;
            line-height: 1;
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 10px;
            border-radius: 5px;
            background-color: #fff;
            box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.08), 0 1px 2px 0 rgba(0, 0, 0, 0.20);
            .label {
              display :inline-block;
              font-size: 14px;
              color: #2f2f2f;
            }
          }
          .item-list {
            border-radius: 6px;
            // border: solid 1px #bbb;
            color: #444;
            background-color: #ffffff;
            box-shadow: 0 23px 15px -12px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(0, 0, 0, 0.25);
            display: none;
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            z-index: 1;
            transition: all 250ms ease;
            transition-delay: 250ms;
            transform: translateY(-10px);
            opacity: 0;
            overflow: hidden;
            &.active {
              display: inline-block;
              transform: translateY(0);
              opacity: 1;
            }
            .item-list-item {
              padding: 10px 12px;
              font-size: 14px;
              &:hover {
                background-color: #ebebeb;
              }
            }
          }
        }
      }
    }
    .register-button {
      position: relative;
      display: inline-block;

      .bubble {
        opacity: 0;
        position: absolute;
        z-index: -1;
        pointer-events: none;
        top: 50%;
        left: calc(100% + 17px);
        transform: translateY(-50%);
        transition: left 0.2s ease,
                    opacity 0.2s ease;
      }

      &:hover {
        .bubble {
          left: calc(100% + 7px);
          opacity: 1;
        }
      }
    }
  }
  .navigation--area {
    display: flex;
    align-items: center;
    gap: 12px;
    .navigation {
      width: 42px;
      height: 42px;
      &.disabled {
        opacity: 0.4;
        cursor: default;
      }

      &:hover:not(.disabled) {
        background-color: #f1f1f1;
      }
    }

    .dropdown {
      position: relative;
      z-index: 1;
      display: inline-block;
      vertical-align: middle;
      width: 174px;
      // margin: 0 8px;
      text-align: left;

      &.disabled {
        pointer-events: none;
        opacity: 0.7;
        cursor: default;
      }

      .selected {
        &.disabled {
          pointer-events: none;
          opacity: 0.7;
          cursor: default;
        }
        border-radius: 5px;
        border: solid 1px #e0e0e0;
        background-color: #ffffff;
        height: 16px;
        padding: 8px 6px 8px 10px;
        cursor: pointer;
        line-height: 16px;

        .index {
          display: inline-block;
          vertical-align: middle;
          height: 16px;
          font-size: 13px;
          font-weight: 500;
          color: #767676;
          padding-right: 8px;

          .current {
            color: #0074FF;
          }

          .slash {
            margin: 0 1px;
          }
        }

        .label {
          display: inline-block;
          vertical-align: middle;
          width: 94px;
          height: 16px;
          font-size: 13px;
          overflow: hidden;
          white-space: nowrap;
          text-overflow: ellipsis;
        }

        .icon {
          position: absolute;
          top: calc(50% - 9px);
          right: 6px;
          vertical-align: middle;
          margin-top: 1px;
        }
      }

      .item-list {
        position: absolute;
        top: -2px;
      left: -31%;
        width: 162%;
        overflow: hidden;
        border-radius: 6px;
        box-shadow: 0 23px 15px -12px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(0, 0, 0, 0.25);
        background-color: #ffffff;
        color: #444444;
        transform-origin: top;
        transform: scaleY(0);
        opacity: 0;
        transition: transform 0.3s ease,
                    opacity 0.2s ease;

        &.active {
          transform: scaleY(1);
          opacity: 1;
        }

        .item-list-wrap {
          max-height: 361px;
          overflow-y: auto;
        }

        .item-list-item {
          display: flex;
          align-items: center;
          gap: 8px;
          width: 100%;
          height: 36px;
          padding: 0 16px 0 12px;
          background-color: #ffffff;
          transition: background-color 0.2s ease;
          text-align: left;
          font-size: 14px;

          .index {
            flex-shrink: 0;
            display: inline-block;
            width: 20px;
            text-align: center;
            vertical-align: middle;
          }

          .type {
            width: 53px;
            &-label {
              padding: 4px 8px;
              border-radius: 25px;
              background: #2C7899;
              color: #FFF;
              font-size: 12px;
              font-weight: 500;
              text-transform: capitalize;
              cursor: default;
              &.exact {
                background: #005BAD;
              }
            }
          }

          .label {
            display: inline-block;
            overflow: hidden;
            white-space: nowrap;
            text-overflow: ellipsis;
            vertical-align: middle;
          }

          &.active {
            background-color: #F4F4F4;
            color: #0074FF;
            .index, .label {
              font-weight: 600;
            }
          }

          &:hover {
            background-color: #ebebeb;
          }

          &:first-child {
            border-top-left-radius: 6px;
            border-top-right-radius: 6px;
          }

          &:last-child {
            border-bottom-left-radius: 6px;
            border-bottom-right-radius: 6px;
          }
        }
      }
    }
  }

  .type--area {
    display: none;
    width: 150px;
    text-align:right;
    opacity: 1;

    .switch {
      position: relative;
      width: 70px;
      height: 34px;
      border-radius: 16px;
      box-shadow: inset 0 1px 1px 0 rgba(0, 0, 0, 0.04);
      border: solid 1px #d7d7d7;
      background-color: rgba(0, 0, 0, 0.06);
      padding: 0 1px;

      &::before {
        position: absolute;
        z-index: 1;
        top: 0;
        right: 0;
        content: "";
        width: 38px;
        height: 32px;
        border-radius: 16px;
        box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.2);
        background-color: #ffffff;
      }

      label {
        display: inline-block;
        cursor: pointer;
        position: relative;
        z-index: 2;
        opacity: 0.5;
        width: 16px;
        height: 16px;
        padding: 8px 8px;

        &.on {
          opacity: 1;
        }
      }
    }
  }
}
</style>
